import { atsApiQuery } from '..';
import { getEmailFormData } from './utils';

export const atsApplicationApi = atsApiQuery.injectEndpoints({
  endpoints: (builder) => {
    return {
      pauseApplication: builder.mutation({
        transformResponse: (response) => response?.data,
        query: ({ applicationId, payload }) => {
          return {
            url: `ats_applications/${applicationId}/pause`,
            method: 'post',
            data: payload,
          };
        },
      }),
      unPauseApplication: builder.mutation({
        transformResponse: (response) => response?.data,
        query: ({ applicationId }) => {
          return {
            url: `ats_applications/${applicationId}/unpause`,
            method: 'post',
          };
        },
      }),
      getApplicationFormsForApplication: builder.query({
        transformResponse: (response) => response?.data,
        query: ({ applicationId }) => {
          return {
            url: `ats_applications/${applicationId}/form`,
            method: 'get',
          };
        },
      }),
      getFilesForApplication: builder.query({
        transformResponse: (response) => response?.data,
        query: ({ applicationId }) => {
          return {
            url: `ats_applications/${applicationId}/show_attachments`,
            method: 'get',
          };
        },
      }),
      addFileForApplication: builder.mutation({
        transformResponse: (response) => response?.data,
        query: ({ applicationId, kind, attachment }) => {
          var formdata = new FormData();
          formdata.append('attachment', attachment);
          formdata.append('kind', kind);
          return {
            url: `/ats_applications/${applicationId}/add_attachment`,
            method: 'post',
            data: formdata,
          };
        },
      }),
      deleteFileFromApplication: builder.mutation({
        transformResponse: (response) => response?.data,
        query: ({ applicationId, attachmentId }) => {
          return {
            url: `/ats_applications/${applicationId}/delete_attachment?attachment_id=${attachmentId}`,
            method: 'delete',
          };
        },
      }),
      uploadResumeForApplication: builder.mutation({
        transformResponse: (response) => response?.data,
        query: ({ applicationId, attachment }) => {
          var formdata = new FormData();
          formdata.append('attachment', attachment);
          return {
            url: `/ats_applications/${applicationId}/upload_resume`,
            method: 'post',
            data: formdata,
          };
        },
      }),
      getInterviewScorecards: builder.query({
        transformResponse: (response) => {
          // api structure might change in future, so we need to handle both cases until its finalized
          return response?.data?.scorecards || response?.data;
        },
        query: ({ applicationId, params }) => {
          return {
            url: `ats_applications/${applicationId}/ats_scorecards`,
            method: 'get',
            params: params,
          };
        },
        providesTags: ['GetScoreCardForInterview'],
      }),
      fetchScorecardById: builder.query({
        transformResponse: (response) => response?.data,
        query: ({ applicationId, scorecardId }) => {
          return {
            url: `ats_applications/${applicationId}/ats_scorecards/${scorecardId}`,
            method: 'get',
          };
        },
        providesTags: ['GetScoreCardForInterview'],
      }),

      getAppDetailsData: builder.mutation({
        transformResponse: (response) => response?.data,
        query: ({ applicationId }) => {
          return {
            url: `ats_applications/${applicationId}/`,
            method: 'get',
          };
        },
      }),

      submitScoreCard: builder.mutation({
        transformResponse: (response) => response?.data,
        query: ({ applicationId, scorecardId, payload }) => {
          return {
            url: `ats_applications/${applicationId}/ats_scorecards/${scorecardId}`,
            method: 'patch',
            data: payload,
          };
        },
        invalidatesTags: () => ['GetScoreCardForInterview'],
      }),
      sendReminderForScorecard: builder.mutation({
        transformResponse: (response) => response.data,
        query: ({ scorecardId }) => {
          return {
            method: 'post',
            url: `ats_scorecards/${scorecardId}/reminder`,
          };
        },
      }),
      moveApplicationStage: builder.mutation({
        transformResponse: (response) => response?.data,
        query: ({ applicationId, payload }) => {
          return {
            url: `ats_applications/${applicationId}/move`,
            method: 'post',
            data: payload,
          };
        },
      }),
      rejectApplication: builder.mutation({
        transformResponse: (response) => response?.data,
        query: ({ applicationId, payload }) => {
          return {
            url: `ats_applications/${applicationId}/reject`,
            method: 'post',
            data: payload,
          };
        },
      }),
      sendEmailForApplication: builder.mutation({
        transformResponse: (response) => response?.data,
        query: ({ params }) => {
          const fd = new FormData();
          const formData = getEmailFormData(fd, params);
          return {
            url: `ats_applications/bulk_email`,
            method: 'post',
            data: formData,
          };
        },
      }),
      bulkRejectApplication: builder.mutation({
        transformResponse: (response) => response?.data,
        query: ({ payload }) => {
          return {
            url: `ats_applications/bulk_reject`,
            method: 'post',
            data: payload,
          };
        },
      }),

      unRejectApplication: builder.mutation({
        transformResponse: (response) => response?.data,
        query: ({ applicationId }) => {
          return {
            url: `ats_applications/${applicationId}/unreject`,
            method: 'post',
          };
        },
      }),
      unRejectBulkApplication: builder.mutation({
        transformResponse: (response) => response?.data,
        query: (payload) => {
          return {
            url: `ats_applications/bulk_active`,
            method: 'post',
            data: payload,
          };
        },
      }),
      getApplicationNotes: builder.mutation({
        transformResponse: (response) => response,
        query: ({ applicationId, params }) => {
          return {
            url: `ats_applications/${applicationId}/notes`,
            method: 'get',
            params,
          };
        },
      }),
      createApplicationNote: builder.mutation({
        transformResponse: (response) => response?.data,
        query: ({ applicationId, payload }) => {
          return {
            url: `ats_applications/${applicationId}/notes`,
            method: 'post',
            data: payload,
          };
        },
      }),
      updateApplicationNote: builder?.mutation({
        transformResponse: (response) => response?.data,
        query: ({ applicationId, payload, noteId }) => {
          return {
            url: `ats_applications/${applicationId}/notes/${noteId}`,
            method: 'patch',
            data: payload,
          };
        },
      }),
      updateApplicationDetails: builder.mutation({
        transformResponse: (response) => response?.data,
        query: ({ applicationId, payload }) => {
          return {
            url: `ats_applications/${applicationId}`,
            method: 'put',
            data: payload,
          };
        },
      }),
      getConversationsForApplication: builder.query({
        transformResponse: (response) => response?.data,
        query: ({ applicationId, params }) => {
          return {
            url: `ats_applications/${applicationId}/candidate_messages`,
            method: 'get',
            params,
          };
        },
      }),
      getScheduledConversationsForApplication: builder.query({
        transformResponse: (response) => response?.data,
        query: ({ applicationId, params }) => {
          return {
            url: `ats_applications/${applicationId}/scheduled_candidate_messages`,
            method: 'get',
            params,
          };
        },
      }),

      scheduleEmail: builder.mutation({
        transformResponse: (response) => response?.data,
        query: ({ payload }) => ({
          url: `/emails/schedule`,
          method: 'post',
          data: payload,
        }),
      }),
      cancelScheduledEmail: builder.mutation({
        transformResponse: (response) => response?.data,
        query: ({ emailId }) => ({
          url: `/emails/${emailId}/cancel-schedule`,
          method: 'post',
        }),
      }),

      // Scorecard PDF generation endpoints
      generateScorecardPDF: builder.mutation({
        transformResponse: (response) => response?.data,
        query: ({ applicationId, payload }) => ({
          url: `ats_applications/${applicationId}/ats_scorecards/generate`,
          method: 'post',
          data: payload,
        }),
      }),
      pollScorecardPDF: builder.query({
        transformResponse: (response) => response?.data,
        query: ({ url }) => ({
          url: url,
          method: 'get',
        }),
      }),
    };
  },
  overrideExisting: true,
});

export const {
  usePauseApplicationMutation,
  useUnPauseApplicationMutation,
  useUnRejectApplicationMutation,
  useUnRejectBulkApplicationMutation,
  useLazyGetInterviewScorecardsQuery,
  useGetInterviewScorecardsQuery,
  useFetchScorecardByIdQuery,
  useSubmitScoreCardMutation,
  useMoveApplicationStageMutation,
  useRejectApplicationMutation,
  useGetApplicationFormsForApplicationQuery,
  useGetFilesForApplicationQuery,
  useAddFileForApplicationMutation,
  useDeleteFileFromApplicationMutation,
  useGetApplicationNotesMutation,
  useCreateApplicationNoteMutation,
  useUpdateApplicationNoteMutation,
  useGetAppDetailsDataMutation,
  useUpdateApplicationDetailsMutation,
  useBulkRejectApplicationMutation,
  useSendEmailForApplicationMutation,
  useGetConversationsForApplicationQuery,
  useGetScheduledConversationsForApplicationQuery,
  useSendReminderForScorecardMutation,
  useUploadResumeForApplicationMutation,
  useScheduleEmailMutation,
  useCancelScheduledEmailMutation,
  useGenerateScorecardPDFMutation,
  useLazyPollScorecardPDFQuery,
} = atsApplicationApi;
